/**
 * Профиль артикула — панель справа (docs/SPEC_2026-10-25_ANALYTICS_PHASE2_3.md, фаза 3).
 *
 * Данные — только слой MCP «Бизнес», факт `productDay` (метрики `pd*`): продажи, воронка, реклама, цена, наличие
 * по дням и плитки периода. Запросы разделены по областям прав: без права на финансы не открывается только блок
 * рекламы. Пустой день — не нулевые продажи: панель хранит лишь активные товар-дни.
 */

import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Telescope } from 'lucide-react'
import { Button, Drawer, MiniKpi, SegmentedControl } from '../../components/ui'
import type { EChartHandle, TimeSeriesEvent, TimeSeriesSeries } from '../../components/ui'
import { QualityNote } from '../analytics/business/QualityNote'
import { ErrorNotice } from '../analytics/shared/fields'
import { explorerErrorText } from '../metrics-explorer/api'
import type { ExplorerResult } from '../metrics-explorer/types'
import { formatAxisValue, formatMetricValue } from '../metrics-explorer/format'
import { splitLimitation } from '../metrics-explorer/labels'
import { ProfileChart } from './ProfileChart'
import { ProfileHeaderBlock } from './ProfileHeaderBlock'
import {
  availabilitySeries,
  buildProfileRequest,
  buildProfileTiles,
  collapsePartialTiles,
  defaultPeriodChoice,
  drrSeries,
  enumerateDays,
  errorKind,
  explorerHref,
  hasPagePeriod,
  parseAttributes,
  periodLabel,
  PERIOD_CHOICES,
  resolvePeriod,
  seriesOf,
  type MarketplaceChoice,
  type PeriodChoice,
  type ProfileGroup,
} from './profileModel'
import type { ArticleProfileContext } from './types'
import { STAGGER_MS, useProfileEvents, useProfileHeader, useProfileQuery } from './useProfileData'
import { useLinkedCrosshair } from './useLinkedCrosshair'

export interface ArticleProfileRequest {
  /** Номер открытия: новое открытие сбрасывает выбор периода и площадки. */
  id: number
  article: string
  context?: ArticleProfileContext
}

type SalesUnit = 'qty' | 'rub'
type FunnelView = 'counts' | 'rates'
type AdsView = 'spend' | 'drr'

const MARKETPLACE_OPTIONS: Array<{ value: MarketplaceChoice; label: string }> = [
  { value: 'all', label: 'Все площадки' },
  { value: 'wb', label: 'WB' },
  { value: 'ozon', label: 'Ozon' },
]

const money = (value: number) => formatMetricValue(value, 'RUB')
const moneyAxis = (value: number) => formatAxisValue(value, 'RUB')
const units = (value: number) => formatMetricValue(value, 'units')
const unitsAxis = (value: number) => formatAxisValue(value, 'units')
const ratio = (value: number) => formatMetricValue(value, 'ratio')
const ratioAxis = (value: number) => formatAxisValue(value, 'ratio')

const OZON_NOTE = 'У Ozon в панели товара нет воронки карточки, рекламы по товару и цены покупателя.'

interface ArticleProfileDrawerProps {
  request: ArticleProfileRequest | null
  onClose: () => void
}

export function ArticleProfileDrawer({ request, onClose }: ArticleProfileDrawerProps) {
  const navigate = useNavigate()
  const article = request?.article ?? null
  const context = request?.context
  const [periodChoice, setPeriodChoice] = useState<PeriodChoice>(() => defaultPeriodChoice(context))
  const [marketplace, setMarketplace] = useState<MarketplaceChoice>(() => context?.marketplace ?? 'all')
  const [salesUnit, setSalesUnit] = useState<SalesUnit>('qty')
  const [funnelView, setFunnelView] = useState<FunnelView>('counts')
  const [adsView, setAdsView] = useState<AdsView>('spend')

  const period = useMemo(() => resolvePeriod(periodChoice, context), [periodChoice, context])
  const days = useMemo(() => enumerateDays(period.from, period.to), [period])
  const scope = article ? { article, from: period.from, to: period.to, marketplace, context } : null
  const bodyOf = (group: ProfileGroup) => (scope ? buildProfileRequest(group, scope) : null)

  const sales = useProfileQuery(bodyOf('sales'), 0)
  const attrs = useProfileQuery(bodyOf('attrs'), STAGGER_MS)
  const funnel = useProfileQuery(bodyOf('funnel'), STAGGER_MS * 2)
  const price = useProfileQuery(bodyOf('price'), STAGGER_MS * 3)
  const stock = useProfileQuery(bodyOf('stock'), STAGGER_MS * 4)
  const ads = useProfileQuery(bodyOf('ads'), STAGGER_MS * 5)

  const { header } = useProfileHeader(article, marketplace, context)
  const pimEvents = useProfileEvents(article, marketplace, context, period)
  const events = useMemo<TimeSeriesEvent[]>(() => pimEvents.map((event) => ({ date: event.date, label: event.label, type: event.type })), [pimEvents])
  const attributes = useMemo(() => parseAttributes(attrs.data), [attrs.data])

  const rawTiles = useMemo(() => buildProfileTiles({ sales, funnel, ads, price, stock }), [sales, funnel, ads, price, stock])
  const { tiles, reason: tilesReason } = useMemo(() => collapsePartialTiles(rawTiles), [rawTiles])

  const of = (group: { data: ExplorerResult | null }, id: string) => seriesOf(group.data, id, days)
  const salesSeries = useMemo<TimeSeriesSeries[]>(
    () =>
      salesUnit === 'qty'
        ? [
            { id: 'orders', name: 'Заказы, шт', values: of(sales, 'pdOrdersQty'), slot: 0 },
            { id: 'buyouts', name: 'Выкупы, шт', values: of(sales, 'pdBuyoutsQty'), slot: 1 },
          ]
        : [
            { id: 'ordersRub', name: 'Заказы, ₽', values: of(sales, 'pdOrdersRub'), slot: 0 },
            { id: 'buyoutsRub', name: 'Выкупы, ₽', values: of(sales, 'pdBuyoutsRub'), slot: 1 },
          ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sales.data, days, salesUnit],
  )
  const funnelSeries = useMemo<TimeSeriesSeries[]>(
    () =>
      funnelView === 'counts'
        ? [
            { id: 'opens', name: 'Открытия карточки', values: of(funnel, 'pdOpenCount'), slot: 0 },
            { id: 'cart', name: 'Корзина', values: of(funnel, 'pdCartCount'), slot: 1 },
          ]
        : [
            { id: 'openToCart', name: 'Открытие → корзина', values: of(funnel, 'pdOpenToCart'), slot: 0 },
            { id: 'cartToOrder', name: 'Корзина → заказ', values: of(funnel, 'pdCartToOrder'), slot: 1 },
            { id: 'openToOrder', name: 'Открытие → заказ', values: of(funnel, 'pdOpenToOrder'), slot: 2 },
          ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [funnel.data, days, funnelView],
  )
  const adsSeries = useMemo<TimeSeriesSeries[]>(
    () =>
      adsView === 'spend'
        ? [{ id: 'adSpend', name: 'Расход рекламы, ₽', values: of(ads, 'pdAdsSpendRub'), slot: 0 }]
        : [{ id: 'drr', name: 'ДРР', values: drrSeries(of(ads, 'pdAdsSpendRub'), of(sales, 'pdOrdersRub')), slot: 0 }],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ads.data, sales.data, days, adsView],
  )
  const priceSeries = useMemo<TimeSeriesSeries[]>(
    () => [
      { id: 'sellerPrice', name: 'Цена продавца', values: of(price, 'pdSellerPrice'), slot: 0 },
      { id: 'buyerPrice', name: 'Цена покупателя WB', values: of(price, 'pdBuyerPrice'), slot: 1 },
      { id: 'cardPrice', name: 'Цена карточки', values: of(price, 'pdCardPrice'), slot: 2 },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [price.data, days],
  )
  const stockSeries = useMemo<TimeSeriesSeries[]>(
    () => [{ id: 'availability', name: 'Наличие', values: availabilitySeries(of(stock, 'pdInStockDays')), slot: 0 }],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stock.data, days],
  )

  const salesRef = useRef<EChartHandle>(null)
  const funnelRef = useRef<EChartHandle>(null)
  const adsRef = useRef<EChartHandle>(null)
  const priceRef = useRef<EChartHandle>(null)
  const stockRef = useRef<EChartHandle>(null)
  const chartRefs = useMemo(() => [salesRef, funnelRef, adsRef, priceRef, stockRef], [])
  useLinkedCrosshair(chartRefs, days, Boolean(article))

  const core = [sales, funnel, price, stock]
  const allForbidden = core.every((group) => group.error && errorKind(group.error) === 'forbidden')
  const allFailed = core.every((group) => group.error)
  const limitations = useMemo(() => {
    const seen = new Set<string>()
    for (const group of [sales, funnel, ads, price, stock]) for (const text of group.data?.limitations ?? []) seen.add(text)
    return [...seen]
  }, [sales.data, funnel.data, ads.data, price.data, stock.data])
  const formulas = useMemo(() => {
    const byId = new Map<string, { id: string; label: string; formula: string }>()
    for (const group of [sales, funnel, ads, price, stock]) for (const metric of group.data?.metrics ?? []) byId.set(metric.id, { id: metric.id, label: metric.label, formula: metric.formula })
    return [...byId.values()]
  }, [sales.data, funnel.data, ads.data, price.data, stock.data])

  const periodOptions = [
    ...(hasPagePeriod(context) ? [{ value: 'page' as PeriodChoice, label: 'Период страницы', hint: periodLabel(context?.from as string, context?.to as string) }] : []),
    ...PERIOD_CHOICES,
  ]
  const ozonOnly = marketplace === 'ozon'
  const retryAll = () => {
    for (const group of [sales, attrs, funnel, price, stock, ads]) if (group.error) group.reload()
  }

  return (
    <Drawer
      isOpen={Boolean(request)}
      onClose={onClose}
      title="Профиль артикула"
      subtitle={article ? `${article} · ${periodLabel(period.from, period.to)}` : undefined}
      size="xl"
      footer={
        scope ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<Telescope className="h-3.5 w-3.5" aria-hidden="true" />}
            title="Откроет Исследователь с теми же метриками и периодом; фильтра по артикулу в ссылке Исследователя пока нет — найдите артикул в таблице"
            onClick={() => {
              navigate(explorerHref(scope, ['pdOrdersQty', 'pdBuyoutsQty', 'pdOpenCount', 'pdSellerPrice']))
              onClose()
            }}
          >
            Открыть в Исследователе
          </Button>
        ) : undefined
      }
    >
      {article && (
        <div className="space-y-5">
          <ProfileHeaderBlock article={article} header={header} attributes={attributes} attributesLoading={attrs.loading} onNavigate={onClose} />

          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl<PeriodChoice> aria-label="Период профиля" size="sm" options={periodOptions} value={periodChoice} onChange={setPeriodChoice} />
            {!context?.marketplace && <SegmentedControl<MarketplaceChoice> aria-label="Площадка" size="sm" options={MARKETPLACE_OPTIONS} value={marketplace} onChange={setMarketplace} />}
          </div>

          {allForbidden ? (
            <div className="space-y-3">
              <ErrorNotice messages={[explorerErrorText(sales.error)]} />
            </div>
          ) : (
            <>
              {allFailed && (
                <div className="space-y-3">
                  <ErrorNotice messages={[explorerErrorText(sales.error)]} />
                  <Button variant="secondary" size="sm" onClick={retryAll}>
                    Повторить
                  </Button>
                </div>
              )}

              {tilesReason && <QualityNote reason={tilesReason} testId="profile-quality-note" />}
              <section aria-label="Итоги периода" className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="profile-tiles">
                {tiles.map((tile) => (
                  <MiniKpi key={tile.id} label={tile.label} value={tile.value} delta={tile.note ?? undefined} />
                ))}
              </section>

              <div className="space-y-4">
                <ProfileChart
                  title="Сколько заказывают и выкупают?"
                  subtitle="По дням; заказы — дата заказа, выкупы — дата продажи"
                  actions={
                    <SegmentedControl<SalesUnit>
                      aria-label="Единица продаж"
                      size="sm"
                      options={[
                        { value: 'qty', label: 'Штуки' },
                        { value: 'rub', label: 'Рубли' },
                      ]}
                      value={salesUnit}
                      onChange={setSalesUnit}
                    />
                  }
                  group={sales}
                  days={days}
                  series={salesSeries}
                  kind="bar"
                  valueFormat={salesUnit === 'qty' ? units : money}
                  axisFormat={salesUnit === 'qty' ? unitsAxis : moneyAxis}
                  events={events}
                  chartRef={salesRef}
                  exportName={`Продажи артикула ${article}`}
                  emptyDescription="В панели товара нет активных дней артикула за период."
                  onRetry={sales.reload}
                />
                <ProfileChart
                  title="Что происходит с карточкой?"
                  subtitle="Открытия, корзина и конверсии воронки карточки WB"
                  actions={
                    <SegmentedControl<FunnelView>
                      aria-label="Показатели воронки"
                      size="sm"
                      options={[
                        { value: 'counts', label: 'Открытия и корзина' },
                        { value: 'rates', label: 'Конверсии' },
                      ]}
                      value={funnelView}
                      onChange={setFunnelView}
                    />
                  }
                  group={funnel}
                  days={days}
                  series={funnelSeries}
                  valueFormat={funnelView === 'counts' ? units : ratio}
                  axisFormat={funnelView === 'counts' ? unitsAxis : ratioAxis}
                  events={events}
                  chartRef={funnelRef}
                  exportName={`Воронка артикула ${article}`}
                  emptyDescription={ozonOnly ? OZON_NOTE : 'Воронки карточки за период нет: артикул не открывали или площадка не отдала данные.'}
                  onRetry={funnel.reload}
                />
                <ProfileChart
                  title="Сколько тратится на рекламу?"
                  subtitle="Прямая реклама WB; ДРР — расход к сумме заказов артикула того же дня"
                  actions={
                    <SegmentedControl<AdsView>
                      aria-label="Показатели рекламы"
                      size="sm"
                      options={[
                        { value: 'spend', label: 'Расход' },
                        { value: 'drr', label: 'ДРР' },
                      ]}
                      value={adsView}
                      onChange={setAdsView}
                    />
                  }
                  group={ads}
                  days={days}
                  series={adsSeries}
                  valueFormat={adsView === 'spend' ? money : ratio}
                  axisFormat={adsView === 'spend' ? moneyAxis : ratioAxis}
                  events={events}
                  chartRef={adsRef}
                  exportName={`Реклама артикула ${article}`}
                  emptyDescription={ozonOnly ? OZON_NOTE : 'Рекламного расхода по артикулу за период нет.'}
                  forbiddenTitle="Нет доступа к рекламе"
                  onRetry={ads.reload}
                />
                <ProfileChart
                  title="По какой цене продают?"
                  subtitle="Цена продавца и покупателя — средневзвешенные по штукам заказов; цена карточки — среднее за день"
                  group={price}
                  days={days}
                  series={priceSeries}
                  fromZero={false}
                  valueFormat={money}
                  axisFormat={moneyAxis}
                  events={events}
                  chartRef={priceRef}
                  exportName={`Цена артикула ${article}`}
                  emptyDescription="Цен за период нет: в дни без заказов цена продавца не определяется."
                  onRetry={price.reload}
                />
                <ProfileChart
                  title="Был ли товар в наличии?"
                  subtitle="День в наличии, если есть остаток на складе площадки или FBS-остаток кабинета; неизвестный день — разрыв"
                  group={stock}
                  days={days}
                  series={stockSeries}
                  kind="area"
                  valueFormat={(value) => (value >= 1 ? 'В наличии' : 'Нет остатка')}
                  axisFormat={(value) => (value === 1 ? 'есть' : value === 0 ? 'нет' : '')}
                  events={events}
                  chartRef={stockRef}
                  exportName={`Наличие артикула ${article}`}
                  emptyDescription="Остатков за период в панели товара нет."
                  onRetry={stock.reload}
                />
              </div>

              <p className="text-xs text-gray-500 dark:text-zinc-400">
                Панель товара хранится с 20.10.2025 и пересобирается ночью: последний день может быть неполным. День без строки — товар не был
                активным (нет открытий, заказов, остатка и рекламы), это не нулевые продажи. Отметки на графиках — события ПИМ: смена цены, акции, фото,
                текст карточки, поставки.
              </p>

              {(formulas.length > 0 || limitations.length > 0) && (
                <details className="rounded-lg border border-gray-200 p-3 text-sm dark:border-zinc-800">
                  <summary className="cursor-pointer font-medium text-gray-900 dark:text-zinc-100">Как посчитано</summary>
                  <div className="mt-3 space-y-4">
                    {formulas.length > 0 && (
                      <ul className="space-y-2">
                        {formulas.map((metric) => (
                          <li key={metric.id}>
                            <p className="text-sm text-gray-900 dark:text-zinc-100">
                              {metric.label} <span className="font-mono text-xs text-gray-500 dark:text-zinc-400">{metric.id}</span>
                            </p>
                            <p className="break-words font-mono text-xs text-gray-600 dark:text-zinc-400">{metric.formula || '—'}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                    {limitations.length > 0 && (
                      <ul className="space-y-2 text-xs text-gray-700 dark:text-zinc-300">
                        {limitations.map((text) => {
                          const part = splitLimitation(text)
                          return (
                            <li key={text}>
                              {part.code && <span className="mr-1 font-mono text-gray-500 dark:text-zinc-400">{part.code}</span>}
                              {part.text}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </Drawer>
  )
}

export default ArticleProfileDrawer
